import React from 'react';
import { Helmet } from 'react-helmet-async';
import useTranslation from '../hooks/useTranslation';

const SEOHead = () => {
  const { t, lang, isRTL } = useTranslation();

  const title = t('seo.title');
  const description = t('seo.description');
  const locale = lang === 'ar' ? 'ar_SA' : 'en_US';
  const canonical = 'https://orkina.com/';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Orkina Media Production',
    alternateName: 'أوركينا لإنتاج الإعلام',
    url: canonical,
    logo: `${canonical}logo-color.jpg`,
    image: `${canonical}logo-color.jpg`,
    description:
      'A full-service media and creative company specializing in TV and cinematic production, digital marketing, media training, publishing, and consulting.',
    telephone: '+971567114651',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Media City (Shams)',
      addressLocality: 'Sharjah',
      addressRegion: 'SH',
      addressCountry: 'AE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 25.3223,
      longitude: 55.5136,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'
      ],
      opens: '09:00',
      closes: '18:00',
    },
    sameAs: [
      'https://www.facebook.com/orkina.online',
      'https://www.instagram.com/orkina.online'
    ],
  };

  return (
    <Helmet>
      {/* Primary */}
      <html lang={lang} dir={isRTL ? 'rtl' : 'ltr'} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="media production, cinematic production, digital marketing, media training, sharjah media city, shams, production house uae, tv production" />
      <link rel="canonical" href={canonical} />

      {/* Language Alternates */}
      <link rel="alternate" hrefLang="en" href={`${canonical}?lang=en`} />
      <link rel="alternate" hrefLang="ar" href={`${canonical}?lang=ar`} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Location SEO */}
      <meta name="geo.region" content="AE-SH" />
      <meta name="geo.placename" content="Sharjah" />
      <meta name="geo.position" content="25.3223;55.5136" />
      <meta name="ICBM" content="25.3223, 55.5136" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image" content={`${canonical}logo-color.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Orkina Media Production" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${canonical}logo-color.jpg`} />

      {/* Extra SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Orkina Media Production" />
      <meta name="theme-color" content="#D4A843" />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SEOHead;
